//libs
import React, { useState, memo, useRef, useMemo, useEffect, useCallback } from 'react';
import isEmpty from 'lodash/isEmpty';
import { useTrackingContext } from 'shared/helpers/analytics/TrackingContext';

// components
import SizeChart from './components/SizeChart';
import SizeButton from './components/SizeButton';
import MeasureWidth from 'shared/components/MeasureWidth';
import SizeGuideBottomSheet from './components/SizeGuideBottomSheet/SizeGuideBottomSheet';
import SelectedSizeChartStrip from 'shared/components/SelectedSizeChartStrip';
import SizeInventory from 'shared/layouts/DetailsPage/components/ProductInfo/components/SizeOptions/components/SizeInventory';

// hooks
import { useSelectedSKUSizeGuide } from 'shared/store/details/hooks/selectors';
import { useRemoteConfig } from 'shared/store/remoteConfigs/hooks';

// constants
import {
  TEXTS,
  TRACKING_KEY,
  SIZE_GUIDE_INTERACTION_LOCATION,
  WIDTH_OFFSET,
  SIZE_SECTION_V2_CONFIG,
  SIZE_GUIDE_SELECTION_MWEB_EXPERIMENT,
  SIZE_GUIDE_SELECTION_DWEB_EXPERIMENT,
  SIZE_GUIDE_SELECTION_MWEB_VARIANT,
  SIZE_GUIDE_SELECTION_DWEB_VARIANT,
  WINDOW_SCROLL_OFFSET,
  SCROLL_DELAY,
} from './texts';
import { SHOW_ANIMATION_KEY } from 'shared/layouts/SizeGuide/Components/SizeGuideComponent/constant';
import NFA from 'shared/helpers/analytics/NFAnalytics';

// helpers
import { useEligibleSSRVariantName } from 'shared/components/ExperimentV2/helpers';

// analytics
import { onReferSizeGuideClickTracking, trackSizeGuideChartClosed } from './analytics';

// styles
import {
  SizeOptionContainer,
  SizeBtnContainer,
  SizeGuideButton,
  TitleContainerV2,
  TertiaryTitleV2,
  SizeGuideText,
  BottomSheetContainer,
  SizeGuideNudgeContainer,
} from './styles';

// helpers
import {
  getSizeOptionsTrackingData,
  updateDataLayerOnSizeSelect,
} from 'shared/components/SizeSelector/helper';
import { SizeTrackingData, trackSizeSelection } from './helpers';
import { SIZE_GUIDE_LOCATION } from 'shared/helpers/analytics/constants/hybridPDP';

type SizeTrackingMetadata = Pick<
  SizeTrackingData,
  'interaction_location' | 'mrp_price' | 'discounted_price' | 'product_id'
>;

export interface ISizeOptionProps {
  sizeOptions: ProductDetail.IProduct['sizeOptions'];
  onSelect: (size: ProductDetail.ISizeOption) => void;
  isOneSize: boolean;
  shake?: boolean;
  currentSize?: ProductDetail.ISizeOption;
  isNewSizeChartEnabled?: boolean;
  isMobile?: boolean;
  sizeGuideDataAvailable?: boolean;
  isBottomSheet?: boolean;
  isOncart?: boolean;
  disabled?: boolean;
  isDesktopWishlist?: boolean;
  sizeTrackingMetadata?: SizeTrackingMetadata;
  catalog_tag?: string[];
  catalogTagFilter?: string;
  addToCart?: () => void;
  isAddedToCart?: boolean;
  hideMeasurementsForCustomSize?: boolean;
  isRevampedQuickView?: boolean;
}

const TABLE_SCROLL_OFFSET = 150;

const getSizeDataForAllSizes = (sizeOptions: ISizeOptionProps['sizeOptions']) => {
  const sizeData = {};
  let conversionCount = 0;
  sizeOptions.options.forEach(({ sku, size_data }) => {
    sizeData[sku] = sizeOptions?.meta?.units?.reduce((acc, { label: unitLabel, id: unitId }) => {
      acc[unitLabel] = sizeOptions?.meta?.columns?.reduce(
        (innerAcc, { id: columnId, type, label: columnLabel, conversion }) => {
          if (conversion) conversionCount += 1;
          innerAcc[columnId] = {
            type,
            value: size_data?.[columnId]?.[unitId],
            label: columnLabel,
            conversion,
            unitLabel,
          };

          return innerAcc;
        },
        {}
      );
      return acc;
    }, {});
  });
  return { sizeData, showToggle: conversionCount !== sizeOptions.options.length };
};

const SizeOptions = ({
  sizeOptions,
  onSelect,
  isOneSize,
  shake,
  currentSize,
  isNewSizeChartEnabled = false,
  isMobile = false,
  sizeGuideDataAvailable = false,
  isBottomSheet = false,
  isOncart = false,
  disabled = false,
  isDesktopWishlist = false,
  sizeTrackingMetadata,
  catalog_tag,
  catalogTagFilter,
  addToCart,
  isAddedToCart,
  hideMeasurementsForCustomSize = false,
  isRevampedQuickView = false,
}: ISizeOptionProps) => {
  const trackingData = useTrackingContext();
  const [openSizeChart, setOpenSizeChart] = useState(false);
  const [measuredWidth, setMeasureWidth] = useState(0);
  const [optimisticSku, setOptimisticSku] = useState<string | undefined>();
  const sizeGuideURL = useSelectedSKUSizeGuide();
  const contRef = useRef<HTMLDivElement>(null);
  const sizeTrackingData = useRef({ selectedSizesList: [], selectedSize: '' });
  // Invalidates cancelled deferred select chains (latest tap wins).
  const selectionEpochRef = useRef(0);
  const pendingRafIdsRef = useRef<number[]>([]);
  const pendingSkuRef = useRef<string | undefined>();
  const isMeasurementChartV2 = useRemoteConfig(SIZE_SECTION_V2_CONFIG, false);
  const { isEligible, variant: sizeGuideVariant } = useEligibleSSRVariantName(
    isMobile ? SIZE_GUIDE_SELECTION_MWEB_EXPERIMENT : SIZE_GUIDE_SELECTION_DWEB_EXPERIMENT
  );
  const isSizeGuideSelectionEnabled = isEligible && sizeGuideVariant === (
    isMobile ? SIZE_GUIDE_SELECTION_MWEB_VARIANT : SIZE_GUIDE_SELECTION_DWEB_VARIANT
  );
  const sizeChartRef = useRef<HTMLDivElement>(null);

  const cancelPendingSelection = useCallback(() => {
    pendingRafIdsRef.current.forEach((id) => cancelAnimationFrame(id));
    pendingRafIdsRef.current = [];
    selectionEpochRef.current += 1;
    pendingSkuRef.current = undefined;
  }, []);

  useEffect(() => {
    if (sizeOptions.options.length === 1) onSelect(sizeOptions.options[0]);
  }, [onSelect, sizeOptions.options]);

  useEffect(() => {
    try {
      if (window.sessionStorage.getItem(SHOW_ANIMATION_KEY) !== 'true')
        window.sessionStorage.setItem(SHOW_ANIMATION_KEY, JSON.stringify(false));
    } catch (err) {
      //do nothing
    }
  }, []);

  // Clear optimistic highlight once Redux/currentSize catches up (successful commit).
  useEffect(() => {
    if (optimisticSku && currentSize?.sku === optimisticSku) {
      if (pendingSkuRef.current === optimisticSku) {
        pendingSkuRef.current = undefined;
      }
      setOptimisticSku(undefined);
    }
  }, [currentSize?.sku, optimisticSku]);

  // Drop any in-flight deferred select on unmount.
  useEffect(() => () => cancelPendingSelection(), [cancelPendingSelection]);

  const selectedSku = optimisticSku ?? currentSize?.sku;
  const isCustomSizeSelected = currentSize?.sizeName?.trim().toLowerCase() === 'custom';

  const rollbackOptimisticToLastCommit = useCallback((epoch: number, sku: string) => {
    // Only roll back if this epoch is still active and UI still shows the failed sku.
    // Rollback target is last successful commitment (currentSize), via clearing optimistic.
    if (epoch !== selectionEpochRef.current) return;
    if (pendingSkuRef.current !== sku) return;
    pendingSkuRef.current = undefined;
    setOptimisticSku((current) => (current === sku ? undefined : current));
  }, []);

  const sizeDataForAllSizes = useMemo(() => getSizeDataForAllSizes(sizeOptions), [sizeOptions]);
  const outOfStockSizeNames = useMemo(
    () => sizeOptions.options.filter((opt) => opt.isOutOfStock === 1).map((opt) => opt.sizeName),
    [sizeOptions.options]
  );
  const stockRemainingMap = useMemo(
    () => sizeOptions.options.reduce((acc, opt) => {
      if (opt.stock_remaining && opt.isOutOfStock !== 1) {
        acc[opt.sizeName] = opt.stock_remaining;
      }
      return acc;
    }, {} as Record<string, number>),
    [sizeOptions.options]
  );
  const openSizeGuideChart = useCallback(() => {
    onReferSizeGuideClickTracking(TRACKING_KEY, Object.keys(trackingData).length ? trackingData : undefined);
    setOpenSizeChart(true);
  }, [trackingData]);

  const onSizeOptionClick = useCallback(
    (sizeOption: ProductDetail.ISizeOption, interactionLocation?: string) => {
      if (disabled) return;

      // Abort previous deferred commit/analytics (same idea as AbortController).
      pendingRafIdsRef.current.forEach((id) => cancelAnimationFrame(id));
      pendingRafIdsRef.current = [];

      const epoch = ++selectionEpochRef.current;
      pendingSkuRef.current = sizeOption.sku;
      setOptimisticSku(sizeOption.sku);

      const outerRafId = requestAnimationFrame(() => {
        const innerRafId = requestAnimationFrame(() => {
          if (epoch !== selectionEpochRef.current) return;

          try {
            if (interactionLocation === SIZE_GUIDE_INTERACTION_LOCATION) {
              NFA.updateDataLayer({ sizeGuide: true });
            }
            onSelect(sizeOption);
          } catch {
            rollbackOptimisticToLastCommit(epoch, sizeOption.sku);
            return;
          }

          if (epoch !== selectionEpochRef.current) return;

          const { availableSizes, outOfStockSizes } = getSizeOptionsTrackingData(
            sizeOptions.options
          );
          const sizesList: string[] = sizeTrackingData.current.selectedSizesList;
          if (isMeasurementChartV2 && isMobile) {
            setTimeout(() => {
              if (epoch !== selectionEpochRef.current) return;

              const bottomPosition =
                sizeChartRef.current?.getBoundingClientRect().bottom + window.pageYOffset;
              const scrollToPosition = bottomPosition - window.innerHeight + TABLE_SCROLL_OFFSET;

              if (
                !isBottomSheet &&
                window.innerHeight - sizeChartRef.current?.getBoundingClientRect().bottom <
                  WINDOW_SCROLL_OFFSET
              ) {
                window.scrollTo({ top: scrollToPosition, behavior: 'smooth' });
              }
            }, SCROLL_DELAY);
          }

          sizesList.push(sizeOption.sizeName);
          sizeTrackingData.current = {
            selectedSizesList: sizesList,
            selectedSize: sizeOption.sizeName,
          };
          trackSizeSelection({
            ...sizeTrackingData.current,
            variantProductID: sizeOption.id,
            catalog_tag,
            catalogTagFilter,
            interactionLocation,
            product_id: sizeOption.parentId || (trackingData.productID as string),
            mrp_price: sizeOption.price,
            discounted_price: sizeOption.discountedPrice,
            ...(Object.keys(trackingData).length
              ? {
                  analyticsContext: {
                    ...trackingData,
                    variantProductID: sizeOption.id,
                    variantType: sizeOption.sizeName,
                    ...(interactionLocation === SIZE_GUIDE_INTERACTION_LOCATION &&
                    trackingData.hybridPDPExpt
                      ? { interactionLocation: SIZE_GUIDE_LOCATION }
                      : {}),
                    MRP: sizeOption.price,
                    discountedPrice: sizeOption.discountedPrice,
                    discountPercent: sizeOption.discount,
                  },
                }
              : {}),
            ...(sizeTrackingMetadata
              ? {
                ...sizeTrackingMetadata,
                product_id: sizeOption.parentId || (trackingData.productID as string) || sizeTrackingMetadata.product_id,
                mrp_price: sizeOption.price ?? sizeTrackingMetadata.mrp_price,
                discounted_price: sizeOption.discountedPrice ?? sizeTrackingMetadata.discounted_price,
                selected_size: sizeOption.sizeName,
                variant_product_id: sizeOption.id,
              }
              : {}),
          });
          updateDataLayerOnSizeSelect(availableSizes, outOfStockSizes, sizeOption);
        });
        pendingRafIdsRef.current.push(innerRafId);
      });
      pendingRafIdsRef.current.push(outerRafId);
    },
    [
      sizeOptions.options,
      isMeasurementChartV2,
      isMobile,
      isBottomSheet,
      onSelect,
      disabled,
      sizeTrackingMetadata,
      catalog_tag,
      catalogTagFilter,
      rollbackOptimisticToLastCommit,
      trackingData,
    ]
  );

  const checkStockStatus = useCallback(() => {
    let greatestString = '';

    for (const val of sizeOptions.options) {
      if (val.sizeName.length > greatestString.length) {
        greatestString = val.sizeName;
      }
    }

    return { greatestString };
  }, [sizeOptions.options]);

  const { greatestString } = checkStockStatus();
  const showSizeGuideButton = isNewSizeChartEnabled
    ? sizeGuideDataAvailable
    : !isEmpty(sizeGuideURL);

  const handleSizeGuideClose = useCallback(() => {
    setOpenSizeChart(false);
    NFA.updateDataLayer({ sizeGuide: false });
    trackSizeGuideChartClosed();
  }, []);

  const handleSizeGuideSelect = useCallback(
    (sizeName: string) => {
      const matchingOption = sizeOptions.options.find((opt) => opt.sizeName === sizeName);
      if (matchingOption) {
        onSizeOptionClick(matchingOption, SIZE_GUIDE_INTERACTION_LOCATION);
      }
    },
    [sizeOptions.options, onSizeOptionClick]
  );

  const handleSizeGuideAddToCart = useCallback(() => {
    addToCart?.();
    handleSizeGuideClose();
  }, [addToCart, handleSizeGuideClose]);

  const selectedSizeChartStripData = useMemo(() => {
    if (!currentSize || !currentSize.sku) {
      return null; // a fallback value if needed
    }
    return (
      sizeDataForAllSizes?.sizeData[currentSize.sku]?.in ||
      sizeDataForAllSizes?.sizeData[currentSize.sku]
    );
  }, [currentSize, sizeDataForAllSizes.sizeData]);

  // handle inventory Nudge for few sizes in limited stock
  if (isEmpty(sizeOptions) || isOneSize) {
    return null;
  }

  return (
    <SizeOptionContainer
      className="size-options-container"
      data-test="size-options-v2"
      shake={shake}
    >
      {!isBottomSheet && (
        <>
          <TitleContainerV2>
            <TertiaryTitleV2>{sizeOptions.title}</TertiaryTitleV2>
            <SizeGuideText>
              {showSizeGuideButton && (
                <SizeGuideButton
                  onClick={openSizeGuideChart}
                  data-test="btn-size-chart"
                  isSizeSelectorV2={true}
                >
                  {TEXTS.SIZE_GUIDE}
                </SizeGuideButton>
              )}
            </SizeGuideText>
          </TitleContainerV2>
          <SizeGuideNudgeContainer>
            <SizeInventory />
          </SizeGuideNudgeContainer>
        </>
      )}

      <SizeBtnContainer ref={contRef}>
        <MeasureWidth key={greatestString} onMeasured={setMeasureWidth}>
          <SizeButton
            onClick={() => { }}
            isSelected={false}
            isOutOfStock={false}
            size={greatestString}
            containerRef={contRef}
            stockRemaining={4}
            isSizeButtonV2={true}
          />
        </MeasureWidth>

        {sizeOptions.options.map((sizeOption, i) => (
          <SizeButton
            key={`size-option-v2-${sizeOption.sku || i}`}
            onClick={() => onSizeOptionClick(sizeOption)}
            isSelected={selectedSku === sizeOption.sku}
            isOutOfStock={sizeOption.isOutOfStock === 1}
            size={sizeOption.sizeName}
            containerRef={contRef}
            stockRemaining={sizeOption.stock_remaining}
            finalWidth={isOncart || measuredWidth === 0 ? 0 : measuredWidth - WIDTH_OFFSET}
            isSizeButtonV2={true}
            disabled={disabled}
            isRevampedQuickView={isRevampedQuickView}
          />
        ))}
      </SizeBtnContainer>

      {isMeasurementChartV2 &&
        currentSize?.sku &&
        !(hideMeasurementsForCustomSize && isCustomSizeSelected) &&
        !isEmpty(sizeDataForAllSizes.sizeData[currentSize.sku]) && (
          <div ref={sizeChartRef}>
            {isBottomSheet ? (
              <BottomSheetContainer data-set="bottomsheet">
                <SelectedSizeChartStrip
                  sizeData={sizeDataForAllSizes.sizeData[currentSize.sku]?.in}
                  sizeName={currentSize.sizeName}
                  isBottomSheet={isBottomSheet}
                  isDesktopWishlist={isDesktopWishlist}
                />
              </BottomSheetContainer>
            ) : (
              <SelectedSizeChartStrip
                sizeData={selectedSizeChartStripData}
                sizeName={currentSize.sizeName}
                isDesktopWishlist={isDesktopWishlist}
              />
            )}
          </div>
        )}

      {!isNewSizeChartEnabled && openSizeChart && (
        <SizeChart sizeGuideURL={sizeGuideURL} onRemove={handleSizeGuideClose} />
      )}

      {isNewSizeChartEnabled && sizeGuideDataAvailable && (
        <SizeGuideBottomSheet
          isMobile={isMobile}
          showSizeChart={openSizeChart}
          onClose={handleSizeGuideClose}
          onSizeSelect={isSizeGuideSelectionEnabled ? handleSizeGuideSelect : undefined}
          selectedSize={isSizeGuideSelectionEnabled ? currentSize : undefined}
          isAddedToCart={isSizeGuideSelectionEnabled ? isAddedToCart : undefined}
          addToCart={isSizeGuideSelectionEnabled ? handleSizeGuideAddToCart : undefined}
          selectedSizeName={isSizeGuideSelectionEnabled ? currentSize?.sizeName : undefined}
          outOfStockSizes={isSizeGuideSelectionEnabled ? outOfStockSizeNames : undefined}
          stockRemainingMap={isSizeGuideSelectionEnabled ? stockRemainingMap : undefined}
        />
      )}
    </SizeOptionContainer>
  );
};

export default memo(SizeOptions);
